import { Component, OnInit, OnDestroy, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';

import { Api } from '../../../api/api';
import { dashboardProjectsControllerGetProjectV1 } from '../../../api/functions';
import { ProjectResponseDashboardDto } from '../../../api/models/project-response-dashboard-dto';
import { HeaderService } from '../../../services/header.service';
import { CountdownProgressCardComponent } from '../../../components/countdown-progress-card/countdown-progress-card.component';
import { RaisedAmountsCardComponent } from '../../../components/raised-amounts-card/raised-amounts-card.component';
import { InvestmentRoundsCardComponent } from '../../../components/investment-rounds-card/investment-rounds-card.component';

@Component({
  selector: 'app-project-insight',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    CountdownProgressCardComponent,
    RaisedAmountsCardComponent,
    InvestmentRoundsCardComponent,
  ],
  templateUrl: './project-insight.component.html',
  styleUrl: './project-insight.component.css',
})
export class ProjectInsightComponent implements OnInit, OnDestroy {
  private readonly api = inject(Api);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);
  private readonly headerService = inject(HeaderService);

  protected readonly project = signal<ProjectResponseDashboardDto | null>(null);
  protected readonly loading = signal(true);
  protected readonly error = signal<string | null>(null);

  private readonly destroy$ = new Subject<void>();
  private projectId: string | null = null;

  ngOnInit(): void {
    // Get project ID from route
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.projectId = params.get('id');
      if (this.projectId) {
        this.loadProject(this.projectId);
      } else {
        this.error.set('No project ID provided');
        this.loading.set(false);
      }
    });
  }

  private async loadProject(id: string): Promise<void> {
    this.loading.set(true);
    this.error.set(null);

    try {
      const project = await this.api.invoke(dashboardProjectsControllerGetProjectV1, { id });
      this.project.set(project);

      // Update header with project name
      this.headerService.setTitle(
        this.translateService.instant('projectInsight.title', { name: project.name })
      );
      this.headerService.setSubtitle(this.translateService.instant('projectInsight.subtitle'));
    } catch (err) {
      console.error('Failed to load project:', err);
      this.error.set('Failed to load project details');
      this.headerService.setTitle(this.translateService.instant('projectInsight.titleError'));
    } finally {
      this.loading.set(false);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
